import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  TextField,
  Checkbox,
  Grid,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { SaveEliATIImportData } from "../../services/InstrumentDataService";
import SendIcon from "@mui/icons-material/Send";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
// Import UserSelectorDialog instead of GetUsers service
import UserSelectorDialog from "../common/UserSelectorDialog";

const EliATI = () => {
  // State management
  const [fileData, setFileData] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isPosting, setIsPosting] = useState(false);

  // Updated analyst state to store user object instead of just string
  const [selectedAnalyst, setSelectedAnalyst] = useState(null);
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);

  // Form fields
  const [selectedProcess, setSelectedProcess] = useState("0005"); // Changed for ATI
  const [batchNumber, setBatchNumber] = useState("");

  // Selection states
  const [selectedSamples, setSelectedSamples] = useState({});
  const [selectedAnalytes, setSelectedAnalytes] = useState({});
  const [selectAllSamples, setSelectAllSamples] = useState(true);
  const [selectAllAnalytes, setSelectAllAnalytes] = useState({});

  // Updated analyte columns for new CSV format - including TDS
  const analyteColumns = [
    "pH",
    "TAlkalinity",
    "Carbonate",
    "Bicarbonate",
    "Conductivity",
    "TDS", // Calculated from Conductivity * 0.66
    "Turbidity",
  ];

  // FIXED: Helper function to check if a value is valid - now accepts 0 as valid
  const isValidAnalyteValue = (value) => {
    // Empty, null, undefined, or whitespace-only values are invalid
    if (
      !value ||
      value.trim() === "" ||
      value === null ||
      value === undefined
    ) {
      return false;
    }

    // "0" is now considered a valid value (this was the main fix needed)
    if (value === "0" || value.trim() === "0") {
      return true;
    }

    // Any other non-empty value is valid
    return value.trim() !== "";
  };

  // Initialize selection states
  useEffect(() => {
    if (processedData.length > 0) {
      // Initialize sample selections
      const sampleSelections = {};
      processedData.forEach((_, index) => {
        sampleSelections[index] = true;
      });
      setSelectedSamples(sampleSelections);

      // FIXED: Initialize analyte selections - now includes 0 values as valid
      const analyteSelections = {};
      const selectAllAnalyteStates = {};
      analyteColumns.forEach((analyte) => {
        analyteSelections[analyte] = {};
        selectAllAnalyteStates[analyte] = false;
        processedData.forEach((sample, index) => {
          // Now auto-select if the analyte has a valid value (including 0)
          const hasValidValue = isValidAnalyteValue(sample.analytes[analyte]);
          analyteSelections[analyte][index] = hasValidValue;
        });
      });
      setSelectedAnalytes(analyteSelections);
      setSelectAllAnalytes(selectAllAnalyteStates);
    }
  }, [processedData]);

  const formatDateForAPI = (date) => {
    if (!date) return null;

    // Create ISO string but preserve local time instead of converting to UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    // Return in ISO format but with local timezone
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "-";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  // Updated analyst selection handlers
  const handleAnalystSelect = (user) => {
    setSelectedAnalyst(user);
  };

  const removeSelectedAnalyst = () => {
    setSelectedAnalyst(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setIsProcessing(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const rows = csvContent.split("\n").map((row) => row.split(","));

        // Store raw data
        setFileData(rows);

        // Process the data
        const processed = processCSVData(rows, csvContent);
        setProcessedData(processed);
        NotificationManager.success(
          "Success",
          `File processed successfully. Found ${processed.length} samples.`
        );
        setIsProcessing(false);
      } catch (error) {
        console.error("Error processing file:", error);
        NotificationManager.error(
          "Errors",
          `Error processing file: ${error.message}`
        );
        setIsProcessing(false);
      }
    };

    reader.readAsText(file);
  };

  const processCSVData = (rows, csvContent) => {
    // For the new format, the header is always the first row
    if (rows.length < 2) {
      throw new Error("File must contain at least header and one data row");
    }

    const headerRow = rows[0];
    const processed = [];

    // Validate header structure
    const expectedHeaders = [
      "sampleID",
      "ResultsPH",
      "TAlkalinity",
      "Carbonate",
      "Bicarbonate",
      "ResultsCondValue",
      "DateTimeStarted",
      "Turbidity",
      "DilutionFactor",
    ];
    const hasValidHeader = expectedHeaders.every((header) =>
      headerRow.some((cell) => cell.trim() === header)
    );

    if (!hasValidHeader) {
      throw new Error("Invalid CSV format. Expected headers not found.");
    }

    // Get column indices
    const columnIndices = {
      sampleID: headerRow.findIndex((h) => h.trim() === "sampleID"),
      pH: headerRow.findIndex((h) => h.trim() === "ResultsPH"),
      TAlkalinity: headerRow.findIndex((h) => h.trim() === "TAlkalinity"),
      Carbonate: headerRow.findIndex((h) => h.trim() === "Carbonate"),
      Bicarbonate: headerRow.findIndex((h) => h.trim() === "Bicarbonate"),
      Conductivity: headerRow.findIndex((h) => h.trim() === "ResultsCondValue"),
      DateTimeStarted: headerRow.findIndex(
        (h) => h.trim() === "DateTimeStarted"
      ),
      Turbidity: headerRow.findIndex((h) => h.trim() === "Turbidity"),
      DilutionFactor: headerRow.findIndex((h) => h.trim() === "DilutionFactor"),
    };

    // Process data rows (starting from row 1)
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length === 0) continue;

      // Get Sample ID
      const sampleId = row[columnIndices.sampleID]?.trim();
      if (!sampleId) continue;

      // Skip empty rows or rows with just commas
      if (row.every((cell) => !cell || cell.trim() === "")) continue;

      const sampleInfo = parseSampleName(sampleId);
      if (!sampleInfo) continue;

      // Get test date
      const dateStarted = row[columnIndices.DateTimeStarted]?.trim();
      let testDateTime = null;
      if (dateStarted) {
        const parsedDate = new Date(dateStarted);
        testDateTime = isNaN(parsedDate.getTime()) ? null : parsedDate;
      }

      const sampleData = {
        originalName: sampleId,
        testDate: testDateTime,
        ...sampleInfo,
        analytes: {},
        isQC: isQCSample(sampleInfo.sampleId),
        scriptTemplate: "", // Not available in new format
      };

      // Map the analyte values from columns
      analyteColumns.forEach((analyte) => {
        if (analyte === "TDS") {
          // TDS column should show conductivity value (not calculated TDS)
          // The 0.66 factor will be applied as dilution factor in the backend
          const conductivityIndex = columnIndices.Conductivity;
          if (conductivityIndex !== -1 && conductivityIndex < row.length) {
            let conductivityValue = row[conductivityIndex]?.trim();
            if (
              conductivityValue &&
              conductivityValue !== "" &&
              conductivityValue !== "n.a." &&
              conductivityValue !== "N.A."
            ) {
              try {
                const condNumValue = parseFloat(conductivityValue);
                if (!isNaN(condNumValue)) {
                  // Store the actual conductivity value, not calculated TDS
                  sampleData.analytes[analyte] = condNumValue.toString();
                } else {
                  sampleData.analytes[analyte] = "";
                }
              } catch {
                sampleData.analytes[analyte] = "";
              }
            } else {
              sampleData.analytes[analyte] = "";
            }
          } else {
            sampleData.analytes[analyte] = "";
          }
        } else {
          const columnIndex = columnIndices[analyte];
          if (columnIndex !== -1 && columnIndex < row.length) {
            let value = row[columnIndex]?.trim();

            // FIXED: Handle empty values - don't convert to "0"
            if (
              value === "" ||
              value === undefined ||
              value === "n.a." ||
              value === "N.A."
            ) {
              value = ""; // Keep empty instead of converting to "0"
            } else {
              try {
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  // FIXED: Keep "0" as "0" - don't filter it out
                  value = numValue.toString();
                } else {
                  value = "";
                }
              } catch {
                value = "";
              }
            }
            sampleData.analytes[analyte] = value;
          } else {
            sampleData.analytes[analyte] = "";
          }
        }
      });

      // Handle dilution factor - if empty, use "1"
      const dilutionFactorValue = row[columnIndices.DilutionFactor]?.trim();
      if (dilutionFactorValue && dilutionFactorValue !== "") {
        sampleData.dilutionFactor = dilutionFactorValue;
      } else {
        sampleData.dilutionFactor = "1"; // Default to 1 if empty
      }

      processed.push(sampleData);
    }

    return processed;
  };

  // FIXED: Updated parseSampleName function to correctly extract order number
  const parseSampleName = (sampleId) => {
    let orderNumber = "";
    let sampleNumber = "";
    let dilutionFactor = "1";
    let notes = "";

    // Handle different sample name formats
    // Examples from new format: "2025071885_2025071885-1", "2025071885-1 DUP", "2025071886_2025071886-1DF5"

    if (sampleId.includes("_")) {
      const parts = sampleId.split("_");
      orderNumber = parts[0]; // FIXED: This is the correct order number (before underscore)
      const remainingPart = parts[1];

      // Extract notes if present (everything after the sample ID part)
      if (remainingPart.includes(" ")) {
        const spaceParts = remainingPart.split(" ");
        // FIXED: Use just the order number, not the full sampleId
        sampleId = spaceParts[0]; // This should be like "2025071885-1"
        notes = spaceParts.slice(1).join(" ");
      } else {
        sampleId = remainingPart; // This should be like "2025071885-1"
      }

      // Extract sample number (part after the dash)
      const dashMatch = remainingPart.match(/-(\d+)/);
      if (dashMatch) {
        sampleNumber = dashMatch[1];
      }

      // Extract dilution factor if present (DF followed by number)
      const dfMatch = remainingPart.match(/DF(\d+)/i);
      if (dfMatch) {
        dilutionFactor = dfMatch[1];
      }
    } else {
      // Handle QC samples and calibration standards that don't follow the order_sample format
      sampleId = sampleId;

      // Check for DUP in the name
      if (sampleId.toUpperCase().includes("DUP")) {
        notes = "DUP";
      }
    }

    if (!sampleId) return null;

    return {
      orderNumber, // FIXED: This now contains just the order number
      sampleId, // FIXED: This now contains the correct sample ID without order number prefix
      sampleNumber,
      dilutionFactor,
      notes,
      formattedName: orderNumber ? `${orderNumber}_${sampleId}` : sampleId,
    };
  };

  const isQCSample = (sampleId) => {
    const qcTypes = [
      "DUP",
      "MS",
      "MSD",
      "BLANK",
      "CCB",
      "CCV",
      "LCCV",
      "QC",
      "STD",
      "CHECK",
      "CALIBRATION",
    ];
    return qcTypes.some((qc) => sampleId.toUpperCase().includes(qc));
  };

  const handleSampleSelection = (index, checked) => {
    setSelectedSamples((prev) => ({
      ...prev,
      [index]: checked,
    }));
  };

  // FIXED: Updated analyte selection to allow 0 values
  const handleAnalyteSelection = (analyte, index, checked) => {
    const sample = processedData[index];
    const analyteValue = sample.analytes[analyte];

    if (checked && !isValidAnalyteValue(analyteValue)) {
      NotificationManager.warning(
        "Warning",
        `Cannot select ${analyte} - no valid value (value is empty)`
      );
      return;
    }

    setSelectedAnalytes((prev) => ({
      ...prev,
      [analyte]: {
        ...prev[analyte],
        [index]: checked,
      },
    }));
  };

  const handleSelectAllSamples = (checked) => {
    setSelectAllSamples(checked);
    const newSelections = {};
    processedData.forEach((_, index) => {
      newSelections[index] = checked;
    });
    setSelectedSamples(newSelections);
  };

  // FIXED: Updated to allow 0 values
  const handleSelectAllAnalytes = (analyte, checked) => {
    setSelectAllAnalytes((prev) => ({
      ...prev,
      [analyte]: checked,
    }));

    setSelectedAnalytes((prev) => ({
      ...prev,
      [analyte]: Object.keys(prev[analyte]).reduce((acc, key) => {
        const index = parseInt(key);
        const sample = processedData[index];
        const analyteValue = sample.analytes[analyte];

        // Only select if checking and has valid value (including 0), or if unchecking
        acc[key] = checked
          ? isValidAnalyteValue(analyteValue)
            ? true
            : false
          : false;
        return acc;
      }, {}),
    }));
  };

  const validateForm = () => {
    if (!fileName) {
      NotificationManager.error("Errors", "Please upload a file first");
      return true;
    }

    if (!batchNumber || parseInt(batchNumber) === 0) {
      NotificationManager.error("Errors", "Please enter a valid batch number");
      return true;
    }

    // Updated validation to check for selected analyst object
    if (!selectedAnalyst || !selectedAnalyst.operator) {
      NotificationManager.error("Errors", "Please select an analyst");
      return true;
    }

    const hasSelectedSamples = Object.values(selectedSamples).some(
      (selected) => selected
    );
    if (!hasSelectedSamples) {
      NotificationManager.error("Errors", "Please select at least one sample");
      return true;
    }

    // FIXED: Check if there are any valid analyte selections (now including 0 values)
    let hasValidAnalyteSelections = false;
    Object.entries(selectedAnalytes).forEach(([analyte, analyteObj]) => {
      Object.entries(analyteObj).forEach(([index, selected]) => {
        if (selected) {
          const sample = processedData[parseInt(index)];
          const analyteValue = sample.analytes[analyte];
          if (isValidAnalyteValue(analyteValue)) {
            hasValidAnalyteSelections = true;
          }
        }
      });
    });

    if (!hasValidAnalyteSelections) {
      NotificationManager.error(
        "Errors",
        "Please select at least one analyte with a valid value"
      );
      return true;
    }

    return false;
  };

  const resetAllData = () => {
    setFileData([]);
    setProcessedData([]);
    setFileName("");
    setBatchNumber("");
    setSelectedAnalyst(null); // Reset analyst selection
    setSelectedSamples({});
    setSelectedAnalytes({});
    setSelectAllSamples(true);
    setSelectAllAnalytes({});
    setIsProcessing(false);
    setIsPosting(false);

    // Reset file input
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const ExcelDateToJSDate = (excelDate, isForOrderCreation = false) => {
    // Excel date calculation (Excel epoch is 1900-01-01, but there's a leap year bug)
    // Subtract 25569 to get days since Unix epoch (1970-01-01)
    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);

    // Don't add extra days - the calculation should be accurate
    // Only adjust if specifically needed for order creation
    if (isForOrderCreation) {
      jsDate.setDate(jsDate.getDate() + 1);
    }

    return jsDate;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (validationErrors) {
      return;
    }

    setIsPosting(true);

    try {
      const samples = [];

      processedData.forEach((sample, index) => {
        if (!selectedSamples[index]) return;

        const selectedAnalyteNames = analyteColumns.filter(
          (analyte) =>
            selectedAnalytes[analyte]?.[index] &&
            isValidAnalyteValue(sample.analytes[analyte])
        );

        if (selectedAnalyteNames.length === 0) return;

        const analyteValues = {};

        analyteColumns.forEach((analyte) => {
          // FIXED: Include analyte if it's selected AND has a valid value (including 0)
          const isSelected = selectedAnalyteNames.includes(analyte);
          const hasValidValue = isValidAnalyteValue(sample.analytes[analyte]);

          // Map the analyte names to match the DTO properties
          let dtoPropertyName = analyte;
          if (analyte === "pH") dtoPropertyName = "pH";
          else if (analyte === "Conductivity") dtoPropertyName = "Conductivity";

          analyteValues[dtoPropertyName] =
            isSelected && hasValidValue ? sample.analytes[analyte] : "";
        });

        samples.push({
          orderNumber: sample.orderNumber,
          sampleId: sample.sampleId,
          testDate: formatDateForAPI(sample.testDate),
          dilutionFactor: sample.dilutionFactor,
          ...analyteValues,
        });
      });

      // FIXED: Updated validation to include 0 values as valid data
      const samplesWithValidData = samples.filter((sample) => {
        return analyteColumns.some(
          (analyte) => sample[analyte] && sample[analyte] !== "" // Removed check for "0"
        );
      });

      if (samplesWithValidData.length === 0) {
        NotificationManager.error(
          "Errors",
          "No samples with valid analyte values found. Cannot proceed with submission."
        );
        setIsPosting(false);
        return;
      }

      const submissionData = {
        fileName,
        processId: selectedProcess,
        batchNumber: parseInt(batchNumber),
        analyst: selectedAnalyst.operator, // Use operator from selected user
        testDate: new Date().toISOString(),
        samples: samplesWithValidData,
      };

      console.log("Final Submission Object:", submissionData);

      NotificationManager.info(
        "Info",
        `Successfully prepared ${samplesWithValidData.length} samples for submission.`
      );

      let response = await SaveEliATIImportData(submissionData);
      NotificationManager.success(
        "Success",
        `Results imported successfully. Selected samples are updated.`
      );

      console.log("API response :  ", response);
      resetAllData();
    } catch (error) {
      console.error("Error preparing submission:", error);
      NotificationManager.error(
        "Errors",
        `Error posting data: ${error.message}`
      );
      setIsPosting(false);
    }
  };

  // FIXED: Updated to show empty values as "-" instead of "0"
  const getAnalyteValue = (sample, analyte) => {
    const value = sample.analytes[analyte];
    if (!value || value === "") {
      return "-"; // Show "-" for truly empty values
    }
    return value; // Show actual value including "0"
  };

  // FIXED: Helper function to determine if an analyte checkbox should be disabled
  const isAnalyteCheckboxDisabled = (sample, analyte, index) => {
    const sampleSelected = selectedSamples[index];
    const analyteValue = sample.analytes[analyte];
    const hasValidValue = isValidAnalyteValue(analyteValue);

    return !sampleSelected || !hasValidValue;
  };

  return (
    <div className="vlp-component-container">
      <span className="title">Automated Titrator Instrument</span>
      <hr />

      <Box
        sx={{ p: 3, maxWidth: "95% !important", margin: "10", padding: "0" }}
      >
        {/* File Upload and Configuration */}
        <>
          <Grid
            container
            spacing={2}
            alignItems="center"
            wrap="wrap"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            {/* Updated analyst selection section */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {selectedAnalyst ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1,
                      border: 1,
                      borderColor: "grey.300",
                      borderRadius: 1,
                      backgroundColor: "grey.50",
                      minHeight: "40px",
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }} size="small">
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        size="small"
                      >
                        {selectedAnalyst.operator} - {selectedAnalyst.aliasName}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setUserSelectorOpen(true)}
                      sx={{ color: "primary.main", ml: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<PersonIcon />}
                    size="small"
                    onClick={() => setUserSelectorOpen(true)}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      height: "40px",
                    }}
                  >
                    Select Analyst
                  </Button>
                )}
              </Box>
            </Grid>
            {selectedAnalyst && (
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <TextField
                  label="QC Batch"
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                  fullWidth
                  type="number"
                  placeholder="Enter batch number"
                  size="small"
                />
              </Grid>
            )}
            {batchNumber !== "" && (
              <Grid item xs={12} sm={4} md={3} lg={2}>
                <input
                  accept=".csv"
                  style={{ display: "none" }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                  disabled={isProcessing}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    size="small"
                    startIcon={<FileUploadIcon />}
                    disabled={isProcessing}
                  >
                    Upload CSV
                  </Button>
                </label>
              </Grid>
            )}
            <Grid item>
              {fileName && (
                <Chip
                  icon={<AttachFileIcon fontSize="small" />}
                  label={fileName}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Grid>
            {processedData.length !== 0 && (
              <Grid item>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={isPosting || processedData.length === 0}
                >
                  {isPosting ? "Posting..." : "Post"}
                </Button>
              </Grid>
            )}
          </Grid>
          <br />
        </>
        <CommonLoader loading={isProcessing || isPosting} />

        {/* Data Table */}
        {processedData.length > 0 && (
          <>
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table sx={{ minWidth: 700 }} size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectAllSamples}
                        onChange={(e) =>
                          handleSelectAllSamples(e.target.checked)
                        }
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <strong>Order Number</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Sample Number</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Sample ID</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Test Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Analyst</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Dilution Factor</strong>
                    </TableCell>
                    {analyteColumns.map((analyte) => (
                      <TableCell key={analyte} align="center">
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Checkbox
                            checked={selectAllAnalytes[analyte] || false}
                            onChange={(e) =>
                              handleSelectAllAnalytes(analyte, e.target.checked)
                            }
                            color="success"
                            size="small"
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            {analyte === "TDS" ? "TDS (Cond.)" : analyte}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedData.map((sample, index) => (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSamples[index] || false}
                          onChange={(e) =>
                            handleSampleSelection(index, e.target.checked)
                          }
                          color="success"
                        />
                      </TableCell>
                      <TableCell>{sample.orderNumber || "-"}</TableCell>
                      <TableCell>{sample.sampleNumber || "-"}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {sample.sampleId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {sample.testDate
                          ? formatDateForDisplay(sample.testDate)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {selectedAnalyst ? selectedAnalyst.operator : "-"}
                      </TableCell>
                      <TableCell>{sample.dilutionFactor || "1"}</TableCell>
                      {analyteColumns.map((analyte) => (
                        <TableCell key={analyte} align="center">
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <Checkbox
                              checked={
                                selectedAnalytes[analyte]?.[index] || false
                              }
                              onChange={(e) =>
                                handleAnalyteSelection(
                                  analyte,
                                  index,
                                  e.target.checked
                                )
                              }
                              color="success"
                              size="small"
                              disabled={isAnalyteCheckboxDisabled(
                                sample,
                                analyte,
                                index
                              )}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8rem",
                                color: isValidAnalyteValue(
                                  sample.analytes[analyte]
                                )
                                  ? "text.primary"
                                  : "text.disabled",
                                fontStyle: isValidAnalyteValue(
                                  sample.analytes[analyte]
                                )
                                  ? "normal"
                                  : "italic",
                              }}
                            >
                              {getAnalyteValue(sample, analyte)}
                            </Typography>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Selected:</strong>{" "}
                {Object.values(selectedSamples).filter(Boolean).length} samples
                |<strong> Total Valid Analyte Measurements:</strong>{" "}
                {Object.entries(selectedAnalytes).reduce(
                  (total, [analyte, analyteObj]) =>
                    total +
                    Object.entries(analyteObj).filter(([index, selected]) => {
                      if (!selected) return false;
                      const sample = processedData[parseInt(index)];
                      return isValidAnalyteValue(sample.analytes[analyte]);
                    }).length,
                  0
                )}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* User Selector Dialog */}
      <UserSelectorDialog
        open={userSelectorOpen}
        onClose={() => setUserSelectorOpen(false)}
        onSelect={handleAnalystSelect}
        multiSelect={false}
        filter="ANALYSTS"
      />
    </div>
  );
};

export default EliATI;
